import { useEffect, useState } from 'react';
import { ApiError, api, type AiStatus, type IdCandidate } from '../api.ts';

/**
 * Версия о минерале ещё до того, как образец сохранён.
 *
 * Спрашивают чаще всего именно здесь: камень в руке, форма открыта, поле
 * «минерал» пустое. Отправлять человека сохранять образец с заглушкой,
 * открывать карточку и спрашивать уже там — значит предложить обойти его
 * же заминку тремя лишними шагами.
 *
 * Выбранное название попадает в поле вместе с достоверностью «требует
 * проверки»: по снимку ничего не подтверждается, и запись об этом должна
 * появляться сама, а не оставаться на совести заполняющего.
 */
export function IdentifyDraft({
  photos,
  localityName,
  localityCountry,
  onPick,
}: {
  photos: Blob[];
  localityName: string | null;
  localityCountry: string | null;
  onPick: (name: string) => void;
}) {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [candidates, setCandidates] = useState<IdCandidate[] | null>(null);
  const [others, setOthers] = useState<IdCandidate[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .aiStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  if (!status?.configured || !photos.length) return null;

  async function ask() {
    setBusy(true);
    setError(null);
    try {
      if (!status?.enabled) await api.updateProfile({ ai_enabled: true });
      const answer = await api.identifyPhotos(photos, localityName, localityCountry);
      setCandidates(answer.candidates);
      setOthers(answer.others);
      setStatus((s) => (s ? { ...s, enabled: true } : s));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Не получилось спросить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="guess-draft">
      <div className="chips">
        <button type="button" className="chip chip--plain" disabled={busy} onClick={ask}>
          {busy ? 'думает…' : candidates ? 'спросить ещё раз' : 'предположить по снимку'}
        </button>
      </div>

      {!status.enabled && !candidates && (
        <p className="hint">
          Снимки уйдут на чужой сервер — всё остальное в druzza никуда не
          отправляется. Первое нажатие спросит разрешение.
        </p>
      )}

      {error && <p className="notice notice--danger">{error}</p>}

      {candidates && (
        <>
          {candidates.length === 0 && <p className="hint">Модель не смогла ничего предложить.</p>}
          {candidates.map((candidate, index) => (
            <button
              type="button"
              key={`${candidate.name}-${index}`}
              className="guess-pick"
              onClick={() => onPick(candidate.name_ru ?? candidate.name)}
            >
              <div className="guess-pick-head">
                <span className="guess-name">{candidate.name_ru ?? candidate.name}</span>
                <span className="guess-confidence">
                  {Math.round(candidate.confidence * 100)}%
                </span>
              </div>
              <span className="hint">
                {candidate.name_ru ? candidate.name : 'нет в справочнике'}
                {candidate.formula ? ` · ${candidate.formula}` : ''}
                {candidate.in_locality ? ' · есть на этом месторождении' : ''}
              </span>
              {candidate.test && (
                <span className="guess-line guess-line--test">
                  <span className="guess-key">проверка</span> {candidate.test}
                </span>
              )}
            </button>
          ))}

          {others.length > 0 && (
            <p className="hint">
              Ещё на образце видно: {others.map((other) => other.name_ru ?? other.name).join(', ')}.
              Их можно добавить в карточке после сохранения.
            </p>
          )}

          <p className="hint">
            Это версия, а не определение: по фотографии не видно ни твёрдости,
            ни черты. Выбранное название встанет с пометкой «требует проверки».
          </p>
        </>
      )}
    </div>
  );
}
